import React, { useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getSubDateRanges, Auth0FetcherWithBody, BarGraph, DateRangeSelector, StaticTable, TimeHorizon, } from "ee-react-components";
import useSWR from "swr";
import { DownloadAsyncFileButton } from "../DownloadAsyncFileButton";
export var Usage = function (_a) {
    var resourceUrl = _a.resourceUrl, startDate = _a.startDate;
    var _b = useState(TimeHorizon.DAY), timeHorizon = _b[0], setTimeHorizon = _b[1];
    var _c = useState([
        new Date(),
        new Date(),
    ]), dateRange = _c[0], setDateRange = _c[1];
    var dateRanges = getSubDateRanges(timeHorizon, dateRange[0]);
    var _d = useAuth0(), isAuthenticated = _d.isAuthenticated, isLoading = _d.isLoading, getAccessTokenSilently = _d.getAccessTokenSilently;
    var _e = useSWR(isLoading || !isAuthenticated
        ? null
        : [
            resourceUrl,
            getAccessTokenSilently,
            dateRanges.map(function (r) { return [r[0], r[1]]; }),
        ], Auth0FetcherWithBody, {
        refreshInterval: timeHorizon === TimeHorizon.DAY ? 60 * 1000 : 0,
    }), stats = _e.data, statsLoading = _e.isLoading;
    var tableData = useMemo(function () {
        return (!statsLoading && stats ? stats : []).map(function (r, i) {
            return {
                time: i < dateRanges.length ? dateRanges[i][2] : "",
                usage: "".concat((r === null || r === void 0 ? void 0 : r.usage) == null ? "-" : "".concat(Number(r.usage * 100).toFixed(1), "%")),
                idle: "".concat((r === null || r === void 0 ? void 0 : r.idle) == null ? "-" : "".concat(Number(r.idle * 100).toFixed(1), "%")),
                offline: "".concat((r === null || r === void 0 ? void 0 : r.unavailable) == null
                    ? "-"
                    : "".concat(Number(r.unavailable * 100).toFixed(1), "%")),
                uptime: "".concat((r === null || r === void 0 ? void 0 : r.uptime) == null ? "-" : "".concat(Number(r.uptime * 100).toFixed(1), "%")),
                energyDispensed: "".concat((r === null || r === void 0 ? void 0 : r.energyDispensedKwh) == null
                    ? "-"
                    : Number(r.energyDispensedKwh).toFixed(1)),
                sessions: "".concat((r === null || r === void 0 ? void 0 : r.successfulSessionCount) == null ? "-" : r.successfulSessionCount),
            };
        });
    }, [stats, statsLoading, dateRanges]);
    var columns = [
        {
            header: "Time",
            accessorKey: "time",
        },
        {
            header: "Station Uptime",
            accessorKey: "uptime",
        },
        {
            header: "Usage",
            accessorKey: "usage",
        },
        {
            header: "Idle",
            accessorKey: "idle",
        },
        {
            header: "Offline",
            accessorKey: "offline",
        },
        {
            header: "Energy Dispensed (kWh)",
            accessorKey: "energyDispensed",
        },
        {
            header: "Sessions",
            accessorKey: "sessions",
            grow: true,
        },
    ];
    var graphData = useMemo(function () {
        return (!statsLoading && stats ? stats : []).map(function (s, i) {
            return {
                timeLabel: i < dateRanges.length ? dateRanges[i][2] : "",
                time: i,
                usage: s ? s.usage : 0,
                idle: s ? s.idle : 0,
                offline: s ? s.unavailable : 0,
            };
        });
    }, [stats, statsLoading, dateRanges]);
    var labelAccessor = function (data) { return data.timeLabel; };
    var yAxisLabelAccessor = function (data) {
        return "".concat(+Number(data * 100).toFixed(1), "%");
    };
    var valueAccessors = [
        {
            label: "Usage",
            accessor: function (data) { return data.usage; },
        },
        {
            label: "Idle",
            accessor: function (data) { return data.idle; },
        },
        {
            label: "Unavailable",
            accessor: function (data) { return data.offline; },
        },
    ];
    var energyGraphData = useMemo(function () {
        return (!statsLoading && stats ? stats : []).map(function (s, i) {
            return {
                timeLabel: i < dateRanges.length ? dateRanges[i][2] : "",
                time: i,
                energyKwh: (s === null || s === void 0 ? void 0 : s.energyDispensedKwh) == null ? 0 : Number(s.energyDispensedKwh),
            };
        });
    }, [stats, statsLoading, dateRanges]);
    var energyLabelAccessor = function (data) { return data.timeLabel; };
    var energyYAxisLabelAccessor = function (data) {
        return "".concat(+Number(data).toFixed(1), " kWh");
    };
    var energyValueAccessors = [
        {
            label: "Energy Delivered",
            accessor: function (data) { return data.energyKwh; },
        },
    ];
    return (React.createElement("div", { className: "flex flex-col gap-y-[32px]" },
        React.createElement(DateRangeSelector, { dateRange: dateRange, setDateRange: setDateRange, firstDate: new Date(startDate), onHorizonChanged: setTimeHorizon, hideCustom: true, hideAll: true }),
        React.createElement("div", { className: "max-w-[1150px]" },
            React.createElement(BarGraph, { data: graphData, labelAccessor: labelAccessor, valueAccessors: valueAccessors, yAxisLabelAccessor: yAxisLabelAccessor, yScale: [0, 1] })),
        React.createElement("div", { className: "max-w-[1150px]" },
            React.createElement(BarGraph, { data: energyGraphData, labelAccessor: energyLabelAccessor, valueAccessors: energyValueAccessors, yAxisLabelAccessor: energyYAxisLabelAccessor })),
        React.createElement("div", { className: "flex flex-col w-full gap-y-4 max-w-[1150px]" },
            React.createElement("div", { className: "ml-auto mr-4" },
                React.createElement(DownloadAsyncFileButton, { resourceUrl: "".concat(resourceUrl, "/csv"), body: dateRanges.map(function (r) { return [r[0], r[1]]; }), labelIdle: "Download CSV", labelLoading: "Generating CSV..." })),
            React.createElement(StaticTable, { data: tableData, columnDefinitions: columns, loading: statsLoading }))));
};
